import React, { useState, useEffect, useMemo, useCallback } from 'react';

const SCRAP_STORAGE_KEY = 'blissScrapInventory';

// Scrap Label Print Modal Component
function ScrapLabelModal({ scrapItems, onClose }) {
  const [labelSize, setLabelSize] = useState('2x3');
  const [selectedItems, setSelectedItems] = useState(
    scrapItems.length === 1 ? [scrapItems[0].id] : []
  );

  const toggleItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedItems(scrapItems.map(s => s.id));
  };

  const selectNone = () => {
    setSelectedItems([]);
  };

  // Format thickness to fractional
  const formatThickness = (thickness) => {
    const map = {
      0.25: '1/4"', 0.3125: '5/16"', 0.375: '3/8"', 0.46875: '15/32"',
      0.5: '1/2"', 0.59375: '19/32"', 0.625: '5/8"', 0.6875: '11/16"',
      0.71875: '23/32"', 0.75: '3/4"', 1.0: '1"', 1.5: '1-1/2"'
    };
    const entries = Object.entries(map);
    const closest = entries.reduce((prev, curr) => 
      Math.abs(parseFloat(curr[0]) - thickness) < Math.abs(parseFloat(prev[0]) - thickness) ? curr : prev
    );
    return closest[1];
  };

  const handlePrintLabels = useCallback(() => {
    const itemsToPrint = scrapItems.filter(s => selectedItems.includes(s.id));
    if (itemsToPrint.length === 0) {
      alert('Please select at least one item to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    const labelWidth = labelSize === '2x3' ? '3in' : '4in';
    const labelHeight = '2in';

    const labelHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Scrap Labels</title>
        <style>
          @page {
            size: ${labelWidth} ${labelHeight};
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
          }
          .label {
            width: ${labelWidth};
            height: ${labelHeight};
            padding: 8px 10px;
            page-break-after: always;
            display: flex;
            flex-direction: column;
            border: 1px dashed #ccc;
          }
          .label:last-child {
            page-break-after: auto;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 4px;
            padding-bottom: 4px;
            border-bottom: 1px solid #333;
          }
          .company {
            font-size: 7px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #666;
          }
          .scrap-badge {
            font-size: 7px;
            font-weight: bold;
            background: #22c55e;
            color: white;
            padding: 2px 6px;
            border-radius: 3px;
          }
          .id-section {
            margin-bottom: 6px;
          }
          .scrap-id {
            font-size: 14px;
            font-weight: bold;
            font-family: monospace;
            letter-spacing: 1px;
          }
          .material {
            font-size: 10px;
            color: #333;
            margin-top: 2px;
          }
          .dimensions-section {
            background: #f5f5f5;
            padding: 6px 8px;
            border-radius: 4px;
            margin-bottom: 6px;
            flex-grow: 1;
          }
          .dimensions-label {
            font-size: 7px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 2px;
          }
          .dimensions {
            font-size: 16px;
            font-weight: bold;
            font-family: monospace;
          }
          .footer {
            display: flex;
            justify-content: space-between;
            font-size: 8px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 4px;
          }
          .location {
            font-weight: 600;
            color: #333;
          }
          .usable-for {
            font-style: italic;
          }
          @media print {
            .label {
              border: none;
            }
          }
        </style>
      </head>
      <body>
        ${itemsToPrint.map(item => {
          const dims = item.partType === 'lumber'
            ? `${item.dimensions?.length}" (${item.dimensions?.dimension || 'N/A'})`
            : `${item.dimensions?.length}" × ${item.dimensions?.width}" × ${formatThickness(item.dimensions?.thickness || 0.75)}`;
          
          return `
            <div class="label">
              <div class="header">
                <span class="company">Bliss Luxury Bunk Co.</span>
                <span class="scrap-badge">♻ SCRAP</span>
              </div>
              <div class="id-section">
                <div class="scrap-id">${item.id?.slice(-8) || 'N/A'}</div>
                <div class="material">${item.material || (item.partType === 'lumber' ? 'Lumber' : 'Plywood')}</div>
              </div>
              <div class="dimensions-section">
                <div class="dimensions-label">Dimensions</div>
                <div class="dimensions">${dims}</div>
              </div>
              <div class="footer">
                <span class="location">${item.location ? `📍 ${item.location}` : ''}</span>
                <span class="usable-for">${item.usableFor ? `Best for: ${item.usableFor}` : ''}</span>
              </div>
            </div>
          `;
        }).join('')}
        <script>
          setTimeout(() => window.print(), 300);
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(labelHTML);
    printWindow.document.close();
  }, [scrapItems, selectedItems, labelSize]);

  const isSingleItem = scrapItems.length === 1;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-charcoal rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden border border-warm-gold/30 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-warm-gold text-xs uppercase tracking-wider mb-1">Print Labels</p>
              <h2 className="text-xl font-light text-white">
                {isSingleItem ? 'Scrap Piece Label' : `${scrapItems.length} Scrap Pieces`}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Label Preview */}
          <div className="bg-white rounded-lg p-4 mb-6">
            <div className="flex justify-between items-start mb-1 pb-1 border-b border-gray-300">
              <span className="text-[7px] font-bold uppercase tracking-wider text-gray-500">Bliss Luxury Bunk Co.</span>
              <span className="text-[7px] font-bold bg-green-500 text-white px-1.5 py-0.5 rounded">♻ SCRAP</span>
            </div>
            <div className="mb-2">
              <div className="text-sm font-bold font-mono text-gray-800">
                {isSingleItem ? scrapItems[0].id?.slice(-8) : 'SCRAP-ID'}
              </div>
              <div className="text-[10px] text-gray-600">
                {isSingleItem ? scrapItems[0].material : 'Material Type'}
              </div>
            </div>
            <div className="bg-gray-100 rounded p-2 mb-2">
              <div className="text-[7px] text-gray-500 uppercase tracking-wider">Dimensions</div>
              <div className="text-base font-bold font-mono text-gray-800">
                {isSingleItem 
                  ? (scrapItems[0].partType === 'lumber'
                    ? `${scrapItems[0].dimensions?.length}" (${scrapItems[0].dimensions?.dimension})`
                    : `${scrapItems[0].dimensions?.length}" × ${scrapItems[0].dimensions?.width}"`)
                  : '48" × 10" × 3/4"'}
              </div>
            </div>
            <div className="flex justify-between text-[8px] text-gray-500 border-t border-gray-200 pt-1">
              <span className="font-semibold text-gray-700">
                {isSingleItem && scrapItems[0].location ? `📍 ${scrapItems[0].location}` : '📍 Location'}
              </span>
              <span className="italic">
                {isSingleItem && scrapItems[0].usableFor ? `Best for: ${scrapItems[0].usableFor}` : ''}
              </span>
            </div>
          </div>

          {/* Label Size */}
          <div className="mb-6">
            <label className="info-label mb-2">Label Size</label>
            <select
              value={labelSize}
              onChange={(e) => setLabelSize(e.target.value)}
              className="select-styled"
            >
              <option value="2x3">2" × 3" (Small)</option>
              <option value="2x4">2" × 4" (Standard)</option>
            </select>
          </div>

          {/* Multi-select for bulk printing */}
          {!isSingleItem && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="info-label">Select Items to Print</label>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-warm-gold hover:underline">
                    Select All
                  </button>
                  <span className="text-gray-custom">|</span>
                  <button onClick={selectNone} className="text-xs text-warm-gold hover:underline">
                    Select None
                  </button>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto bg-charcoal-light rounded-lg border border-white/10">
                {scrapItems.map(item => (
                  <label 
                    key={item.id}
                    className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItem(item.id)}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: '#c9a959' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-mono">{item.id?.slice(-8)}</div>
                      <div className="text-xs text-gray-light truncate">{item.material}</div>
                    </div>
                    <div className="text-xs text-gray-custom">
                      {item.dimensions?.length}" × {item.dimensions?.width}"
                    </div>
                  </label>
                ))}
              </div>
              <div className="text-xs text-gray-custom mt-2">
                {selectedItems.length} of {scrapItems.length} selected
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10">
          <button
            onClick={handlePrintLabels}
            disabled={!isSingleItem && selectedItems.length === 0}
            className="btn-gold w-full py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Print {isSingleItem ? 'Label' : `${selectedItems.length} Label${selectedItems.length !== 1 ? 's' : ''}`}
          </button>
          <p className="text-gray-custom text-xs text-center mt-3">
            Use label paper or a label printer for best results
          </p>
        </div>
      </div>
    </div>
  );
}

// Part type options
const PART_TYPES = [
  { value: 'plywood', label: 'Plywood' },
  { value: 'lumber', label: 'Lumber' },
  { value: 'mdf', label: 'MDF' },
  { value: 'hardboard', label: 'Hardboard' },
  { value: 'melamine', label: 'Melamine' },
];

// Wood species options
const WOOD_SPECIES = [
  { value: 'pine', label: 'Pine' },
  { value: 'poplar', label: 'Poplar' },
  { value: 'oak', label: 'Oak' },
  { value: 'maple', label: 'Maple' },
  { value: 'walnut', label: 'Walnut' },
  { value: 'birch', label: 'Birch' },
  { value: 'cherry', label: 'Cherry' },
  { value: 'alder', label: 'Alder' },
  { value: 'ash', label: 'Ash' },
  { value: 'cedar', label: 'Cedar' },
  { value: 'baltic_birch', label: 'Baltic Birch Ply' },
  { value: 'mdf', label: 'MDF (No Species)' },
  { value: 'other', label: 'Other' },
];

// Lumber dimensions
const LUMBER_DIMENSIONS = [
  '1x2', '1x3', '1x4', '1x6', '1x8', '1x10', '1x12',
  '2x2', '2x3', '2x4', '2x6', '2x8', '2x10', '2x12',
  '4x4', '4x6', '6x6',
];

// Common plywood/sheet goods thicknesses (fractional)
const SHEET_THICKNESSES = [
  { value: 0.25, label: '1/4"', decimal: '0.25' },
  { value: 0.3125, label: '5/16"', decimal: '0.3125' },
  { value: 0.375, label: '3/8"', decimal: '0.375' },
  { value: 0.46875, label: '15/32"', decimal: '0.469' },
  { value: 0.5, label: '1/2"', decimal: '0.5' },
  { value: 0.59375, label: '19/32"', decimal: '0.594' },
  { value: 0.625, label: '5/8"', decimal: '0.625' },
  { value: 0.6875, label: '11/16"', decimal: '0.688' },
  { value: 0.71875, label: '23/32"', decimal: '0.719' },
  { value: 0.75, label: '3/4"', decimal: '0.75' },
  { value: 1.0, label: '1"', decimal: '1.0' },
  { value: 1.5, label: '1-1/2"', decimal: '1.5' },
];

function getScrapInventory() {
  try {
    const stored = localStorage.getItem(SCRAP_STORAGE_KEY);
    if (!stored) return [];
    const data = JSON.parse(stored);
    // Convert old format (object with plywood/lumber arrays) to new format (flat array)
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const flatArray = [];
      if (data.plywood) flatArray.push(...data.plywood.map(s => ({ ...s, partType: 'plywood' })));
      if (data.lumber) flatArray.push(...data.lumber.map(s => ({ ...s, partType: 'lumber' })));
      return flatArray;
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error reading scrap inventory:', error);
    return [];
  }
}

function saveScrapInventory(inventory) {
  try {
    localStorage.setItem(SCRAP_STORAGE_KEY, JSON.stringify(inventory));
    // Also save in old format for backwards compatibility with work order
    const oldFormat = {
      plywood: inventory.filter(s => s.partType === 'plywood' || s.partType === 'mdf' || s.partType === 'melamine' || s.partType === 'hardboard'),
      lumber: inventory.filter(s => s.partType === 'lumber'),
    };
    localStorage.setItem(SCRAP_STORAGE_KEY, JSON.stringify(oldFormat));
  } catch (error) {
    console.error('Error saving scrap inventory:', error);
  }
}

export default function ScrapBank() {
  const [inventory, setInventory] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPartType, setFilterPartType] = useState('all');
  const [filterSpecies, setFilterSpecies] = useState('all');
  const [filterStatus, setFilterStatus] = useState('available');
  const [sortBy, setSortBy] = useState('dateAdded');
  const [sortOrder, setSortOrder] = useState('desc');
  const [labelModal, setLabelModal] = useState({ show: false, items: [] });

  // New scrap form state
  const [formData, setFormData] = useState({
    partType: 'plywood',
    species: 'pine',
    length: '',
    width: '',
    thickness: 0.75, // Default to 3/4"
    lumberDimension: '2x4',
    location: '',
    notes: '',
    usableFor: '',
    quantity: 1, // Number of pieces to add
  });

  useEffect(() => {
    const scrap = getScrapInventory();
    // Convert old format if needed
    if (scrap && typeof scrap === 'object' && !Array.isArray(scrap)) {
      const flatArray = [];
      if (scrap.plywood) {
        flatArray.push(...scrap.plywood.map(s => ({ 
          ...s, 
          partType: 'plywood',
          dimensions: s.dimensions || { length: 0, width: 0 }
        })));
      }
      if (scrap.lumber) {
        flatArray.push(...scrap.lumber.map(s => ({ 
          ...s, 
          partType: 'lumber',
          dimensions: s.dimensions || { length: 0 }
        })));
      }
      setInventory(flatArray);
    } else {
      setInventory(Array.isArray(scrap) ? scrap : []);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateId = () => {
    return `SCRAP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`.toUpperCase();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const quantity = parseInt(formData.quantity) || 1;
    const baseScrap = {
      partType: formData.partType,
      species: formData.species,
      material: `${WOOD_SPECIES.find(s => s.value === formData.species)?.label || formData.species} ${PART_TYPES.find(t => t.value === formData.partType)?.label || formData.partType}`,
      dimensions: formData.partType === 'lumber' 
        ? { 
            length: parseFloat(formData.length) || 0,
            dimension: formData.lumberDimension,
          }
        : {
            length: parseFloat(formData.length) || 0,
            width: parseFloat(formData.width) || 0,
            thickness: parseFloat(formData.thickness) || 0.75,
          },
      location: formData.location,
      notes: formData.notes,
      usableFor: formData.usableFor,
      used: false,
      usedFor: null,
      usedDate: null,
    };

    let updatedInventory;
    if (editingItem) {
      // When editing, just update the single item
      const editedScrap = {
        ...baseScrap,
        id: editingItem.id,
        dateAdded: editingItem.dateAdded,
        dateModified: new Date().toISOString(),
        used: editingItem.used,
        usedFor: editingItem.usedFor,
        usedDate: editingItem.usedDate,
      };
      updatedInventory = inventory.map(item => 
        item.id === editingItem.id ? editedScrap : item
      );
    } else {
      // When adding new, create multiple pieces with unique IDs
      const newPieces = [];
      for (let i = 0; i < quantity; i++) {
        newPieces.push({
          ...baseScrap,
          id: generateId(), // Each piece gets unique ID
          dateAdded: new Date().toISOString(),
          dateModified: new Date().toISOString(),
        });
      }
      updatedInventory = [...inventory, ...newPieces];
    }

    setInventory(updatedInventory);
    saveScrapInventory(updatedInventory);
    resetForm();
    
    // Show confirmation for multiple pieces
    if (!editingItem && quantity > 1) {
      alert(`Added ${quantity} scrap pieces to inventory!`);
    }
  };

  const resetForm = () => {
    setFormData({
      partType: 'plywood',
      species: 'pine',
      length: '',
      width: '',
      thickness: 0.75,
      lumberDimension: '2x4',
      location: '',
      notes: '',
      usableFor: '',
      quantity: 1,
    });
    setShowAddForm(false);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Find the closest matching thickness value from our options
    const itemThickness = item.dimensions?.thickness || 0.75;
    const closestThickness = SHEET_THICKNESSES.reduce((prev, curr) => 
      Math.abs(curr.value - itemThickness) < Math.abs(prev.value - itemThickness) ? curr : prev
    ).value;
    
    setFormData({
      partType: item.partType || 'plywood',
      species: item.species || 'pine',
      length: item.dimensions?.length?.toString() || '',
      width: item.dimensions?.width?.toString() || '',
      thickness: closestThickness,
      lumberDimension: item.dimensions?.dimension || '2x4',
      location: item.location || '',
      notes: item.notes || '',
      usableFor: item.usableFor || '',
      quantity: 1, // Editing is always single piece
    });
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this scrap piece?')) {
      const updatedInventory = inventory.filter(item => item.id !== id);
      setInventory(updatedInventory);
      saveScrapInventory(updatedInventory);
    }
  };

  const handleMarkUsed = (id) => {
    const updatedInventory = inventory.map(item => 
      item.id === id 
        ? { ...item, used: true, usedDate: new Date().toISOString() }
        : item
    );
    setInventory(updatedInventory);
    saveScrapInventory(updatedInventory);
  };

  const handleMarkAvailable = (id) => {
    const updatedInventory = inventory.map(item => 
      item.id === id 
        ? { ...item, used: false, usedFor: null, usedDate: null }
        : item
    );
    setInventory(updatedInventory);
    saveScrapInventory(updatedInventory);
  };

  // Filter and sort inventory
  const filteredInventory = useMemo(() => {
    let result = [...inventory];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.id?.toLowerCase().includes(term) ||
        item.material?.toLowerCase().includes(term) ||
        item.species?.toLowerCase().includes(term) ||
        item.location?.toLowerCase().includes(term) ||
        item.notes?.toLowerCase().includes(term) ||
        item.usableFor?.toLowerCase().includes(term)
      );
    }

    // Filter by part type
    if (filterPartType !== 'all') {
      result = result.filter(item => item.partType === filterPartType);
    }

    // Filter by species
    if (filterSpecies !== 'all') {
      result = result.filter(item => item.species === filterSpecies);
    }

    // Filter by status
    if (filterStatus === 'available') {
      result = result.filter(item => !item.used);
    } else if (filterStatus === 'used') {
      result = result.filter(item => item.used);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'dateAdded':
          comparison = new Date(b.dateAdded) - new Date(a.dateAdded);
          break;
        case 'partType':
          comparison = (a.partType || '').localeCompare(b.partType || '');
          break;
        case 'species':
          comparison = (a.species || '').localeCompare(b.species || '');
          break;
        case 'length':
          comparison = (b.dimensions?.length || 0) - (a.dimensions?.length || 0);
          break;
        case 'location':
          comparison = (a.location || '').localeCompare(b.location || '');
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? -comparison : comparison;
    });

    return result;
  }, [inventory, searchTerm, filterPartType, filterSpecies, filterStatus, sortBy, sortOrder]);

  // Group by part type for summary
  const summary = useMemo(() => {
    const available = inventory.filter(item => !item.used);
    const byType = {};
    const bySpecies = {};
    
    available.forEach(item => {
      const type = item.partType || 'other';
      const species = item.species || 'unknown';
      
      byType[type] = (byType[type] || 0) + 1;
      bySpecies[species] = (bySpecies[species] || 0) + 1;
    });

    return {
      total: inventory.length,
      available: available.length,
      used: inventory.length - available.length,
      byType,
      bySpecies,
    };
  }, [inventory]);

  // Convert decimal thickness to fractional display
  const formatThickness = (thickness) => {
    const match = SHEET_THICKNESSES.find(t => Math.abs(t.value - thickness) < 0.01);
    if (match) return match.label;
    return `${thickness}"`;
  };

  const formatDimensions = (item) => {
    if (item.partType === 'lumber') {
      return `${item.dimensions?.length || 0}" length (${item.dimensions?.dimension || 'N/A'})`;
    }
    const thickness = item.dimensions?.thickness || 0.75;
    return `${item.dimensions?.length || 0}" × ${item.dimensions?.width || 0}" × ${formatThickness(thickness)}`;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-light text-white mb-2">
              <span className="text-warm-gold">♻️</span> Scrap Bank Inventory
            </h1>
            <p className="text-gray-custom text-sm">
              Manage physical scrap pieces for substitution into fabrication work orders
            </p>
          </div>
          <div className="flex gap-2">
            {filteredInventory.length > 0 && (
              <button
                onClick={() => setLabelModal({ show: true, items: filteredInventory.filter(i => !i.used) })}
                className="btn-gold-outline px-4 py-3 text-sm flex items-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                Print Labels
              </button>
            )}
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-gold px-6 py-3 text-sm flex items-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Scrap Piece
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-warm-gold mb-1">{summary.available}</div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Available</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-gray-light mb-1">{summary.used}</div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Used</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-green-400 mb-1">
            {summary.byType.plywood || 0}
          </div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Plywood</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-blue-400 mb-1">
            {summary.byType.lumber || 0}
          </div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Lumber</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Search scrap..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-styled"
            />
          </div>
          
          {/* Filter by Part Type */}
          <div>
            <select
              value={filterPartType}
              onChange={(e) => setFilterPartType(e.target.value)}
              className="select-styled"
            >
              <option value="all">All Types</option>
              {PART_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          {/* Filter by Species */}
          <div>
            <select
              value={filterSpecies}
              onChange={(e) => setFilterSpecies(e.target.value)}
              className="select-styled"
            >
              <option value="all">All Species</option>
              {WOOD_SPECIES.map(species => (
                <option key={species.value} value={species.value}>{species.label}</option>
              ))}
            </select>
          </div>
          
          {/* Filter by Status */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="select-styled"
            >
              <option value="available">Available Only</option>
              <option value="used">Used Only</option>
              <option value="all">All Status</option>
            </select>
          </div>
          
          {/* Sort */}
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="select-styled flex-1"
            >
              <option value="dateAdded">Date Added</option>
              <option value="partType">Part Type</option>
              <option value="species">Species</option>
              <option value="length">Length</option>
              <option value="location">Location</option>
            </select>
            <button
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 bg-charcoal-light border border-white/10 rounded-lg text-white hover:border-warm-gold/50 transition"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="card p-6 overflow-x-auto">
        {filteredInventory.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📦</div>
            <p className="text-gray-custom text-lg mb-2">No scrap pieces found</p>
            <p className="text-gray-custom text-sm mb-6">
              {inventory.length === 0 
                ? "Add your first scrap piece to get started"
                : "Try adjusting your filters"}
            </p>
            {inventory.length === 0 && (
              <button
                onClick={() => setShowAddForm(true)}
                className="btn-gold-outline px-6 py-2 text-sm"
              >
                Add First Scrap Piece
              </button>
            )}
          </div>
        ) : (
          <table className="table-styled">
            <thead>
              <tr>
                <th>ID</th>
                <th>Part Type</th>
                <th>Species</th>
                <th>Dimensions</th>
                <th>Location</th>
                <th>Usable For</th>
                <th>Status</th>
                <th>Date Added</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item) => (
                <tr key={item.id} className={item.used ? 'opacity-50' : ''}>
                  <td>
                    <span className="font-mono text-xs text-warm-gold">{item.id?.slice(-8) || 'N/A'}</span>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.partType === 'plywood' ? 'bg-green-500/20 text-green-400' :
                      item.partType === 'lumber' ? 'bg-blue-500/20 text-blue-400' :
                      item.partType === 'mdf' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {PART_TYPES.find(t => t.value === item.partType)?.label || item.partType}
                    </span>
                  </td>
                  <td className="text-gray-light">
                    {WOOD_SPECIES.find(s => s.value === item.species)?.label || item.species || 'N/A'}
                  </td>
                  <td className="text-white font-medium">
                    {formatDimensions(item)}
                  </td>
                  <td className="text-gray-light">{item.location || '—'}</td>
                  <td className="text-gray-light text-sm">{item.usableFor || '—'}</td>
                  <td>
                    {item.used ? (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                        Used
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
                        Available
                      </span>
                    )}
                  </td>
                  <td className="text-gray-custom text-sm">
                    {new Date(item.dateAdded).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setLabelModal({ show: true, items: [item] })}
                        className="p-2 text-gray-light hover:text-green-400 transition"
                        title="Print Label"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 6 2 18 2 18 9"/>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                          <rect x="6" y="14" width="12" height="8"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          // Duplicate: pre-fill form with this item's dimensions
                          const itemThickness = item.dimensions?.thickness || 0.75;
                          const closestThickness = SHEET_THICKNESSES.reduce((prev, curr) => 
                            Math.abs(curr.value - itemThickness) < Math.abs(prev.value - itemThickness) ? curr : prev
                          ).value;
                          setFormData({
                            partType: item.partType || 'plywood',
                            species: item.species || 'pine',
                            length: item.dimensions?.length?.toString() || '',
                            width: item.dimensions?.width?.toString() || '',
                            thickness: closestThickness,
                            lumberDimension: item.dimensions?.dimension || '2x4',
                            location: item.location || '',
                            notes: item.notes || '',
                            usableFor: item.usableFor || '',
                            quantity: 1,
                          });
                          setEditingItem(null); // Not editing, creating new
                          setShowAddForm(true);
                        }}
                        className="p-2 text-gray-light hover:text-blue-400 transition"
                        title="Add More Like This"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-gray-light hover:text-warm-gold transition"
                        title="Edit"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      {item.used ? (
                        <button
                          onClick={() => handleMarkAvailable(item.id)}
                          className="p-2 text-gray-light hover:text-green-400 transition"
                          title="Mark Available"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="1 4 1 10 7 10"/>
                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMarkUsed(item.id)}
                          className="p-2 text-gray-light hover:text-orange-400 transition"
                          title="Mark Used"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-gray-light hover:text-red-400 transition"
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick Stats by Species */}
      {Object.keys(summary.bySpecies).length > 0 && (
        <div className="card p-6 mt-6">
          <h3 className="text-sm uppercase tracking-wider text-warm-gold mb-4">Available by Wood Species</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.bySpecies)
              .sort((a, b) => b[1] - a[1])
              .map(([species, count]) => (
                <span 
                  key={species}
                  className="px-3 py-2 bg-charcoal-light rounded-lg text-sm cursor-pointer hover:bg-warm-gold/20 transition"
                  onClick={() => setFilterSpecies(species)}
                >
                  <span className="text-white font-medium">{count}</span>
                  <span className="text-gray-custom ml-2">
                    {WOOD_SPECIES.find(s => s.value === species)?.label || species}
                  </span>
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-charcoal rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-warm-gold/30">
            {/* Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-warm-gold text-xs uppercase tracking-wider mb-1">
                    {editingItem ? 'Edit Scrap Piece' : 'Add New Scrap'}
                  </p>
                  <h2 className="text-xl font-light text-white">Enter Physical Dimensions</h2>
                </div>
                <button
                  onClick={resetForm}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* Part Type */}
                <div>
                  <label className="info-label mb-2">Part Type *</label>
                  <select
                    name="partType"
                    value={formData.partType}
                    onChange={handleInputChange}
                    className="select-styled"
                    required
                  >
                    {PART_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Wood Species */}
                <div>
                  <label className="info-label mb-2">Wood Species *</label>
                  <select
                    name="species"
                    value={formData.species}
                    onChange={handleInputChange}
                    className="select-styled"
                    required
                  >
                    {WOOD_SPECIES.map(species => (
                      <option key={species.value} value={species.value}>{species.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dimensions */}
              <div className="mb-6">
                <h3 className="text-sm uppercase tracking-wider text-warm-gold mb-3">Dimensions</h3>
                
                {formData.partType === 'lumber' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="info-label mb-2">Length (inches) *</label>
                      <input
                        type="number"
                        name="length"
                        value={formData.length}
                        onChange={handleInputChange}
                        className="input-styled"
                        placeholder="e.g., 48"
                        step="0.125"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="info-label mb-2">Lumber Size *</label>
                      <select
                        name="lumberDimension"
                        value={formData.lumberDimension}
                        onChange={handleInputChange}
                        className="select-styled"
                        required
                      >
                        {LUMBER_DIMENSIONS.map(dim => (
                          <option key={dim} value={dim}>{dim}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="info-label mb-2">Length (inches) *</label>
                      <input
                        type="number"
                        name="length"
                        value={formData.length}
                        onChange={handleInputChange}
                        className="input-styled"
                        placeholder="e.g., 48"
                        step="0.125"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="info-label mb-2">Width (inches) *</label>
                      <input
                        type="number"
                        name="width"
                        value={formData.width}
                        onChange={handleInputChange}
                        className="input-styled"
                        placeholder="e.g., 24"
                        step="0.125"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="info-label mb-2">Thickness *</label>
                      <select
                        name="thickness"
                        value={formData.thickness}
                        onChange={(e) => setFormData(prev => ({ ...prev, thickness: parseFloat(e.target.value) }))}
                        className="select-styled"
                        required
                      >
                        {SHEET_THICKNESSES.map(t => (
                          <option key={t.value} value={t.value}>
                            {t.label} ({t.decimal}")
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Quantity - Only show when adding new (not editing) */}
              {!editingItem && (
                <div className="mb-6 p-4 bg-warm-gold/10 border border-warm-gold/30 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="info-label mb-2">How Many Pieces? *</label>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, quantity: Math.max(1, (prev.quantity || 1) - 1) }))}
                          className="w-10 h-10 rounded-lg bg-charcoal-light border border-white/20 text-white hover:border-warm-gold transition flex items-center justify-center text-xl font-bold"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          name="quantity"
                          value={formData.quantity}
                          onChange={(e) => setFormData(prev => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
                          className="input-styled w-24 text-center text-xl font-bold"
                          min="1"
                          max="100"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, quantity: Math.min(100, (prev.quantity || 1) + 1) }))}
                          className="w-10 h-10 rounded-lg bg-charcoal-light border border-white/20 text-white hover:border-warm-gold transition flex items-center justify-center text-xl font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-warm-gold text-sm font-medium">
                        {formData.quantity > 1 ? `Adding ${formData.quantity} identical pieces` : 'Adding 1 piece'}
                      </p>
                      <p className="text-gray-custom text-xs">Each will get a unique ID</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="info-label mb-2">Storage Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="input-styled"
                    placeholder="e.g., Rack A, Bin 3"
                  />
                </div>
                <div>
                  <label className="info-label mb-2">Usable For</label>
                  <input
                    type="text"
                    name="usableFor"
                    value={formData.usableFor}
                    onChange={handleInputChange}
                    className="input-styled"
                    placeholder="e.g., Side panels, Rails"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="info-label mb-2">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="input-styled min-h-[80px] resize-y"
                  placeholder="Any additional notes about this scrap piece..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button type="submit" className="btn-gold flex-1 py-3 flex items-center justify-center gap-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {editingItem 
                    ? 'Update Scrap Piece' 
                    : formData.quantity > 1 
                      ? `Add ${formData.quantity} Pieces to Inventory`
                      : 'Add to Inventory'
                  }
                </button>
                <button 
                  type="button" 
                  onClick={resetForm}
                  className="btn-gold-outline px-6 py-3"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Label Print Modal */}
      {labelModal.show && labelModal.items.length > 0 && (
        <ScrapLabelModal
          scrapItems={labelModal.items}
          onClose={() => setLabelModal({ show: false, items: [] })}
        />
      )}
    </div>
  );
}
